import { useEffect, useState, useRef, useMemo, type ReactElement } from 'react';
import type { Props } from './types.js';
import { Video } from '#~/livekit/components/video/index.js';
import { traceVideoAttachment } from '#~/livekit/components/video/attachment-trace.js';
import { useBandwidthDeviceState, useRoomLiveKit, useVideoTrack } from '#~/livekit/hooks.js';
import { peekProxy } from '#~/agent/utils.js';
import { useDepot } from '#~/depot/hooks.js';
import { observeDevice, unobserveDevice } from '#~/livekit/viewport-tracker.js';
import {
  createStreamRecoveryOwner,
  type StreamRecoveryOwner
} from '#~/analytics/domains/stream-recovery.js';
import { useTrialCameraLocked } from '#~/api/billing/camera-access.js';

/** `Viewer` component. */
export const Viewer = ({
  deviceId,
  children,
  onGovernorState,
  onPresentedFrame,
  lockedFallback,
  ...rootProps
}: Props): ReactElement => {
  // ENG-4898: after an app restart the restored source row renders this tile before its
  // Bridge proxy owns the device, and the source tray memoizes the tile on unchanged props.
  // A render-time lookup would keep that miss (no room, no LiveKit, no track) until the
  // operator selects the camera. Resolve the owner inside a depot selector instead: state
  // sync writes the depot on every accepted device transaction, so the tile re-renders the
  // moment its proxy can be located. `peekProxy` is read-only, so the selector has no side
  // effects. Per rule: think-in-systems (ai.knowledge/global).
  const proxy = useDepot(() => peekProxy(deviceId));
  const locked = useTrialCameraLocked(proxy?.roomId, proxy?.id, deviceId);
  const livekit = useRoomLiveKit(proxy?.roomId);
  const roomId = livekit?.id;
  // A track reset replaces only the inner view. A changed camera, proxy or room
  // instead gets a separate owner, so no replacement adopts foreign outcomes.
  const recoveryOwner = useMemo(
    () =>
      createStreamRecoveryOwner({
        device_id: deviceId,
        ...(proxy?.id !== undefined ? { agent_id: proxy.id } : {}),
        ...(typeof proxy?.embedded === 'boolean' ? { is_embedded: proxy.embedded } : {})
      }),
    [deviceId, proxy?.id, proxy?.roomId, roomId, proxy?.embedded]
  );
  const [refreshKey, setRefreshKey] = useState(0);
  const [instanceID] = useState(Math.random().toString(36).substr(2, 9));
  const viewerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // The outer view can temporarily have no Video child during replacement;
    // hidden-page cancellation must also cover that gap.
    const cancelHidden = (): void => {
      if (document.visibilityState === 'hidden') recoveryOwner.cancel();
    };
    document.addEventListener('visibilitychange', cancelHidden);
    return () => {
      document.removeEventListener('visibilitychange', cancelHidden);
      recoveryOwner.dispose();
    };
  }, [recoveryOwner]);

  useEffect(() => {
    livekit?.addReattachListener(deviceId + '-' + instanceID, () => {
      setRefreshKey((prev) => prev + 1);
    });

    return () => {
      livekit?.removeReattachListener(deviceId + '-' + instanceID);
    };
  }, [livekit, deviceId]);

  useEffect(() => {
    const element = viewerRef.current;
    if (!roomId || !element) return;

    // Tier 2 is left to LiveKit's native adaptive stream, not governor pausing.
    observeDevice(roomId, deviceId, element);
    return () => unobserveDevice(roomId, deviceId, element);
  }, [roomId, deviceId]);

  return (
    <div ref={viewerRef} {...rootProps}>
      {/* Keep underlying ongoing outputs untouched; locked previews never attach video. */}
      {locked ? (
        // Match the normal video's intrinsic size so a locked source card keeps its preview height.
        (lockedFallback ?? (
          <div
            className='aspect-video h-full w-full rounded-inherit bg-base-300'
            aria-label='Camera locked'
          />
        ))
      ) : (
        <ViewerInner
          className='h-full w-full rounded-inherit'
          deviceId={deviceId}
          recoveryOwner={recoveryOwner}
          key={'viewer-' + refreshKey + '-' + instanceID}
          onGovernorState={onGovernorState}
          onPresentedFrame={onPresentedFrame}
        />
      )}
      {children}
    </div>
  );
};

export const ViewerInner = ({
  deviceId,
  onGovernorState,
  onPresentedFrame,
  recoveryOwner,
  ...props
}: Props & { recoveryOwner?: StreamRecoveryOwner }): ReactElement => {
  // ENG-4898: same read-only owner lookup as `Viewer`, for the same restored-tile reason.
  const proxy = useDepot(() => peekProxy(deviceId));
  const livekit = useRoomLiveKit(proxy?.roomId);
  const track = useVideoTrack(deviceId, proxy?.roomId);
  const governorState = useBandwidthDeviceState(deviceId, proxy?.roomId);
  const [instanceID] = useState(Math.random().toString(36).substr(2, 9));
  const [_, setConnectionStatus] = useState('unknown');

  // Ref to store the callback for livekit event
  const onLiveKitChangedRef = useRef<(() => void) | null>(null);

  // Publish the committed governor state to an outer observer (the Add preview)
  // so it can suspend its own frame budget instead of reporting a deliberate
  // pause as unavailable video.
  useEffect(() => {
    onGovernorState?.(governorState);
  }, [governorState, onGovernorState]);

  useEffect(() => {
    const interval = setInterval(() => {
      setConnectionStatus(track?.streamState ?? 'unknown');
    }, 1000);

    return () => {
      clearInterval(interval);
    };
  }, [livekit, proxy, track]);

  useEffect(() => {
    if (!livekit) return;
    const handler = () => {
      if (onLiveKitChangedRef.current) {
        if (!document.hidden) {
          livekit?.triggerFocusListener();
          onLiveKitChangedRef.current();
        }
      }
    };
    livekit.on('changed', handler);
    return () => {
      livekit.off('changed', handler);
    };
  }, [livekit]);

  const handleStall = () => {
    // Suppress stall recovery when the bandwidth governor has intentionally
    // paused this device — resetting the subscription would fight the governor's
    // congestion management and cause flickering. Only proceed with stall
    // recovery for devices that the governor considers active.
    // Renamed to avoid shadowing the outer `governorState` from useBandwidthDeviceState
    const currentGovernorState = livekit?.getBandwidthDeviceState(deviceId);
    const paused =
      currentGovernorState === 'paused-auto' || currentGovernorState === 'paused-manual';
    traceVideoAttachment(
      'viewer.stall',
      { deviceId, instanceId: `viewer:${instanceID}` },
      null,
      track,
      {
        roomId: proxy?.roomId,
        livekitPresent: !!livekit,
        governorState: currentGovernorState,
        action: paused
          ? 'skip-governor-paused'
          : livekit
            ? 'reset-cloud-subscription'
            : 'skip-no-room'
      }
    );
    if (paused) return;
    livekit?.resetDeviceCloudSubscription(deviceId);
  };

  // Pass the ref setter to Video
  return (
    <Video
      track={track}
      traceDeviceId={deviceId}
      key={`${track?.mediaStreamID ?? ''}-${deviceId}-${instanceID}`}
      {...props}
      muted
      deviceId={deviceId}
      {...(proxy?.roomId ? { roomId: proxy.roomId } : {})}
      {...(recoveryOwner !== undefined ? { recoveryOwner } : {})}
      {...(proxy?.id !== undefined ? { agentId: proxy.id } : {})}
      {...(typeof proxy?.embedded === 'boolean' ? { isEmbedded: proxy.embedded } : {})}
      governorState={governorState}
      handleStall={handleStall}
      onPresentedFrame={onPresentedFrame}
      setOnLiveKitChanged={(cb: () => void) => {
        onLiveKitChangedRef.current = cb;
      }}
    />
  );
};
